package er.prateek.crossly.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import er.prateek.crossly.game.crossword.CellPos
import er.prateek.crossly.ui.screens.crossword.GameKey
import er.prateek.crossly.ui.screens.crossword.LetterDragState
import er.prateek.crossly.ui.screens.crossword.draggableLetter
import er.prateek.crossly.ui.theme.Spacing

/**
 * Numeric keypad for the math modes: digits 0-9, a backspace, and an optional submit key. Built
 * from the same extruded [GameKey] face as the crossword keyboard so the two input surfaces share
 * one physical language. Submit is only shown when [onSubmit] is non-null (riddles enter an
 * answer here; the math crossword checks via its action bar instead).
 */
@Composable
fun NumberPad(
    onDigit: (Char) -> Unit,
    onBackspace: () -> Unit,
    modifier: Modifier = Modifier,
    onSubmit: (() -> Unit)? = null,
    submitEnabled: Boolean = true,
    keyHeight: Dp = 52.dp,
    /** When set, digit keys can also be dragged onto a board square. */
    dragState: LetterDragState? = null,
    onDropOnCell: (CellPos, Char) -> Unit = { _, _ -> },
    /** Applied to the backspace key alone, e.g. so a tour can spotlight it. */
    backspaceModifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Spacing.xs),
    ) {
        listOf("123", "456", "789").forEach { row ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
            ) {
                row.forEach { digit ->
                    GameKey(
                        modifier = Modifier.weight(1f).draggableLetter(digit, dragState, onDropped = onDropOnCell),
                        height = keyHeight,
                        onClick = { onDigit(digit) },
                    ) { tint ->
                        Text(digit.toString(), style = MaterialTheme.typography.titleLarge, color = tint)
                    }
                }
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
        ) {
            GameKey(
                modifier = Modifier.weight(1f).then(backspaceModifier),
                height = keyHeight,
                emphasized = true,
                onClick = onBackspace,
            ) { tint -> IconBackspace(size = 22.dp, tint = tint) }
            GameKey(
                modifier = Modifier.weight(1f).draggableLetter('0', dragState, onDropped = onDropOnCell),
                height = keyHeight,
                onClick = { onDigit('0') },
            ) { tint ->
                Text("0", style = MaterialTheme.typography.titleLarge, color = tint)
            }
            if (onSubmit != null) {
                GameKey(
                    modifier = Modifier.weight(1f),
                    height = keyHeight,
                    emphasized = true,
                    enabled = submitEnabled,
                    onClick = onSubmit,
                ) { tint -> IconCheck(size = 22.dp, tint = tint) }
            } else {
                Box(modifier = Modifier.weight(1f))
            }
        }
    }
}
